import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Dimensions,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { PhoneInputComponent } from '../components/design-system/PhoneInput';
import { Input } from '../components/design-system/Input';
import { Button } from '../components/design-system/Button';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService } from '../services/api';
import { ScreenLayout } from '../components/ScreenLayout';
import { Header } from '../components/Header';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768 || screenHeight < 768);

export const WhatsAppContactScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { user, currentBusiness, currentSubscription } = useAppStore();
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  
  // Set active tab to home on mount
  useEffect(() => {
    setActiveTab('home');
  }, [setActiveTab]);
  
  const [contactName, setContactName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [topic, setTopic] = useState('');
  const [startConversation, setStartConversation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingContact, setIsCheckingContact] = useState(false);
  const [existingContact, setExistingContact] = useState<any>(null);
  const [showContactNameField, setShowContactNameField] = useState(false);
  const [messageLimits, setMessageLimits] = useState<{ canSend: boolean; message: string; current: number; max: number } | null>(null);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);
  const phoneInputRef = React.useRef<any>(null);

  // Load message limits when screen opens
  const loadMessageLimits = async () => {
    try {
      setIsLoadingLimits(true);
      const response = await apiService.checkWhatsAppLimits();
      if (response.statusCode === 1000 && response.data) {
        setMessageLimits(response.data);
      }
    } catch (error) {
      console.error('Error loading message limits:', error);
      // Set default limits if API fails
      setMessageLimits({ canSend: true, message: 'Unable to load limits', current: 0, max: 500 });
    } finally {
      setIsLoadingLimits(false);
    }
  };

  // Reset form when screen opens
  useEffect(() => {
    setContactName('');
    setPhoneNumber('');
    setTopic('');
    setStartConversation(false);
    setExistingContact(null);
    setShowContactNameField(false);
    loadMessageLimits();
  }, []);

  // Check if contact exists when phone number changes
  useEffect(() => {
    const checkExistingContact = async () => {
      if (!phoneNumber.trim() || phoneNumber.length < 8) {
        setExistingContact(null);
        setShowContactNameField(false);
        return;
      }

      try {
        setIsCheckingContact(true);
        
        // Get formatted phone number
        let formattedPhoneNumber = phoneNumber;
        if (phoneInputRef.current) {
          const phoneData = phoneInputRef.current.getValue();
          if (phoneData?.formattedNumber) {
            formattedPhoneNumber = phoneData.formattedNumber;
          } else if (phoneData?.dialCode && !phoneNumber.startsWith('+')) {
            formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
          }
        }

        // Ensure phone number starts with +
        if (!formattedPhoneNumber.startsWith('+')) {
          if (phoneInputRef.current) {
            const phoneData = phoneInputRef.current.getValue();
            if (phoneData?.dialCode) {
              formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
            } else {
              formattedPhoneNumber = `+${phoneNumber}`;
            }
          } else {
            formattedPhoneNumber = `+${phoneNumber}`;
          }
        }

        // Check if contact exists
        const response = await apiService.checkContactExists(formattedPhoneNumber);
        
        if (response.statusCode === 1000 && response.data?.exists) {
          setExistingContact(response.data.contact);
          setContactName(response.data.contact.name || '');
          setShowContactNameField(true); // Show field with existing name
        } else {
          setExistingContact(null);
          setContactName('');
          setShowContactNameField(true); // Show field for new contact
        }
      } catch (error) {
        console.error('Error checking existing contact:', error);
        setExistingContact(null);
        setContactName('');
        setShowContactNameField(true); // Show field even on error
      } finally {
        setIsCheckingContact(false);
      }
    };

    const timeoutId = setTimeout(checkExistingContact, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [phoneNumber]);

  const handleSave = async () => {
    if (!phoneNumber.trim()) {
      Alert.alert('Missing Information', 'Please enter a phone number.');
      return;
    }

    if (!showContactNameField || !contactName.trim()) {
      Alert.alert('Missing Information', 'Please wait for contact verification or enter a valid phone number.');
      return;
    }

    if (startConversation && !topic.trim()) {
      Alert.alert('Missing Information', 'Please enter a topic for the conversation.');
      return;
    }

    if (!currentBusiness || !currentSubscription) {
      Alert.alert('Configuration Error', 'No business or subscription found.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Get formatted phone number
      let formattedPhoneNumber = phoneNumber;
      if (phoneInputRef.current) {
        const phoneData = phoneInputRef.current.getValue();
        if (phoneData?.formattedNumber) {
          formattedPhoneNumber = phoneData.formattedNumber;
        } else if (phoneData?.dialCode && !phoneNumber.startsWith('+')) {
          formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
        }
      }

      // Ensure phone number starts with +
      if (!formattedPhoneNumber.startsWith('+')) {
        if (phoneInputRef.current) {
          const phoneData = phoneInputRef.current.getValue();
          if (phoneData?.dialCode) {
            formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
          } else {
            formattedPhoneNumber = `+${phoneNumber}`;
          }
        } else {
          formattedPhoneNumber = `+${phoneNumber}`;
        }
      }

      // Validate phone number - support different country formats
      if (formattedPhoneNumber.length < 8) {
        Alert.alert('Invalid Phone Number', 'Please enter a valid phone number.');
        return;
      }

      if (startConversation) {
        // Start conversation with template message
        await startWhatsAppConversation(formattedPhoneNumber, contactName.trim(), topic.trim());
      } else {
        // Just add contact to database
        await addContactToDatabase(formattedPhoneNumber, contactName.trim());
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      Alert.alert('Error', 'Failed to save contact. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const addContactToDatabase = async (formattedPhoneNumber: string, contactName: string) => {
    try {
      // If contact already exists, don't update the name
      if (existingContact) {
        showNotification('info', 'Contact Exists', `Contact with this number already exists: ${existingContact.name}`);
        navigateTo('home');
        return;
      }

      // Check if parent contact already exists
      try {
        const searchResponse = await apiService.searchParentContactsByIdentifiers({
          phone: formattedPhoneNumber,
        });
        
        if (searchResponse.statusCode === 1000 && searchResponse.data && Array.isArray(searchResponse.data) && searchResponse.data.length > 0) {
          // Parent contact already exists - update it if needed
          const existingParent = searchResponse.data[0];
          if (contactName.trim() && contactName.trim() !== existingParent.profile?.name) {
            await apiService.updateParentContact(existingParent._id || existingParent.id, {
              profile: {
                name: contactName.trim(),
              },
            });
          }
          
          showNotification('success', 'Contact Added', `${contactName || existingParent.profile?.name || formattedPhoneNumber} has been added to your contacts`);
          navigateTo('home');
          return;
        }
      } catch (error) {
        console.error('Error checking for existing parent contact:', error);
      }
      
      // Create new parent contact with WhatsApp channel
      try {
        const createResponse = await apiService.createParentContact({
          primaryIdentifier: formattedPhoneNumber,
          primaryIdentifierType: 'phone',
          identifiers: {
            phone: formattedPhoneNumber,
            whatsapp: formattedPhoneNumber,
          },
          profile: {
            name: contactName.trim() || formattedPhoneNumber,
            phone: formattedPhoneNumber,
          },
          channels: [{
            channel: 'whatsapp',
            channelIdentifier: formattedPhoneNumber,
          }],
        });
        
        if (createResponse.statusCode === 1000 && createResponse.data) {
          showNotification('success', 'Contact Added', `${contactName || formattedPhoneNumber} has been added to your contacts`);
          navigateTo('home');
        } else {
          throw new Error(createResponse.errors?.[0]?.message || 'Failed to create parent contact');
        }
      } catch (error: any) {
        console.error('Error creating parent contact:', error);
        // Fallback to old API if parent contact creation fails
        const response = await apiService.addContact({
          name: contactName,
          phoneNumber: formattedPhoneNumber,
          businessId: currentBusiness?._id,
        });

        if (response.statusCode === 1000) {
          showNotification('success', 'Contact Added', `${contactName} has been added to your contacts`);
          navigateTo('home');
        } else {
          throw new Error(response.errors?.[0]?.message || 'Failed to add contact');
        }
      }
    } catch (error) {
      console.error('Error adding contact to database:', error);
      throw error;
    }
  };

  const startWhatsAppConversation = async (formattedPhoneNumber: string, contactName: string, topic: string) => {
    try {
      // Check message limits before proceeding
      const limitsResponse = await apiService.checkWhatsAppLimits();
      if (limitsResponse.statusCode === 1000 && limitsResponse.data) {
        const { canSend, message, current, max } = limitsResponse.data;
        if (!canSend) {
          Alert.alert(
            'Message Limit Reached', 
            `${message}\n\nCurrent: ${current}/${max} billable messages`,
            [{ text: 'OK' }]
          );
          return;
        }
      }

      // Use existing contact name if contact already exists
      const finalContactName = existingContact ? existingContact.name : contactName;
      
      // Get WhatsApp credentials
      const wabuService = currentSubscription?.services?.wabu as any;
      const whatsappCredentials = wabuService?.metadata?.whatsapp_credentials || {};
      
      if (!wabuService?.metadata?.whatsapp_phone?.length) {
        Alert.alert('Configuration Error', 'No WhatsApp phone number configured for this business.');
        return;
      }

      const phone = wabuService.metadata.whatsapp_phone[0];
      const phoneId = phone.whatsapp_phone_number_id || phone.phone_id || phone.id || phone.phone_number_id;

      if (!phoneId) {
        Alert.alert('Configuration Error', 'Invalid WhatsApp phone number configuration.');
        return;
      }

      // Template parameters for conversation
      const templateParameters = [
        currentBusiness?.name || 'DISLIX', // Header: business_name
        topic // Body: user's custom message
      ];

      // Send template message
      const response = await apiService.sendWhatsAppTemplateMessage({
        to: formattedPhoneNumber,
        templateName: 'open_window_request',
        businessPhoneNumberId: phoneId,
        parameters: templateParameters,
        reason: topic,
        accessToken: whatsappCredentials.access_token,
        businessAccountId: whatsappCredentials.business_account_id,
        businessId: currentBusiness?._id,
        contactName: finalContactName, // Use the final contact name (existing or new)
        userId: user?.id,
        contactId: existingContact?.id,
      });

      if (response.statusCode === 1000) {
        const userName = user?.firstName + ' ' + user?.lastName || 'User';
        showNotification('success', 'Conversation Started', `WhatsApp conversation with ${finalContactName} has been initiated by ${userName}`);
        navigateTo('home');
      } else {
        throw new Error(response.errors?.[0]?.message || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting WhatsApp conversation:', error);
      throw error;
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={false}
          showMessages={false}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <View style={styles.whatsappIcon}>
              <Ionicons name="logo-whatsapp" size={24} color="white" />
            </View>
            <View style={styles.headerText}>
              <Text style={styles.title}>WhatsApp Contact</Text>
              <Text style={styles.subtitle}>Add contact and start conversations</Text>
            </View>
          </View>
        </View>

        {/* Business Info */}
        <View style={styles.businessInfo}>
          <Text style={styles.businessName}>{currentBusiness?.name}</Text>
          <Text style={styles.businessSubtext}>
            {startConversation ? 'Add contact and start conversation' : 'Add contact to database'}
          </Text>
        </View>

        {/* Form Container */}
        <View style={styles.formContainer}>
          {/* Message Limits Progress Bar */}
          {messageLimits && (
            <View style={styles.limitsContainer}>
              <View style={styles.limitsHeader}>
                <Text style={styles.limitsTitle}>Customer Sessions</Text>
                {isLoadingLimits ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Text style={styles.limitsCount}>
                    {messageLimits.current}/{messageLimits.max}
                  </Text>
                )}
              </View>
              <View style={styles.progressBarContainer}>
                <View 
                  style={[
                    styles.progressBar, 
                    { 
                      width: `${Math.min((messageLimits.current / messageLimits.max) * 100, 100)}%`,
                      backgroundColor: messageLimits.current >= messageLimits.max ? colors.error : colors.primary
                    }
                  ]} 
                />
              </View>
              <Text style={[
                styles.limitsMessage,
                { color: messageLimits.current >= messageLimits.max ? colors.error : colors.text.secondary }
              ]}>
                {messageLimits.message}
              </Text>
            </View>
          )}

          {/* Phone Number - First field */}
          <View style={styles.phoneInputSection}>
            <Text style={styles.phoneLabel}>Phone Number *</Text>
            <PhoneInputComponent
              ref={phoneInputRef}
              value={phoneNumber}
              onChangeText={setPhoneNumber}
              onChange={setPhoneNumber}
              placeholder="Enter phone number"
              style={styles.phoneInput}
            />
            {!phoneNumber.trim() && isLoading && (
              <Text style={styles.errorText}>Phone number is required</Text>
            )}
            
            {/* Existing Contact Warning */}
            {existingContact && (
              <View style={styles.existingContactWarning}>
                <Ionicons name="checkmark-circle" size={16} color={colors.success} />
                <Text style={styles.existingContactText}>
                  Contact found: {existingContact.name}. Using existing contact data.
                </Text>
              </View>
            )}
            
            {/* Checking Contact Indicator */}
            {isCheckingContact && (
              <View style={styles.checkingContact}>
                <ActivityIndicator size="small" color={colors.primary} />
                <Text style={styles.checkingContactText}>Checking existing contacts...</Text>
              </View>
            )}
          </View>

          {/* Contact Name - Only show when phone number is entered */}
          {showContactNameField && (
            <Input
              label={existingContact ? "Contact Name (Existing)" : "Contact Name *"}
              value={contactName}
              onChangeText={existingContact ? undefined : setContactName}
              placeholder={existingContact ? "Contact name from database" : "Enter contact name"}
              autoCapitalize="words"
              editable={!existingContact}
              inputStyle={existingContact ? styles.disabledInput : undefined}
              error={!contactName.trim() && isLoading ? 'Contact name is required' : undefined}
            />
          )}

          {/* Start Conversation Toggle */}
          <View style={styles.toggleSection}>
            <View style={styles.toggleLabel}>
              <Text style={styles.toggleTitle}>Start Conversation</Text>
              <Text style={styles.toggleSubtitle}>
                Send a template message to open 24-hour window
              </Text>
              {messageLimits && !messageLimits.canSend && (
                <Text style={styles.limitReachedText}>
                  ⚠️ Customer session limit reached - Cannot start new conversations
                </Text>
              )}
            </View>
            <Switch
              value={startConversation}
              onValueChange={setStartConversation}
              trackColor={{ 
                false: messageLimits ? (!messageLimits.canSend ? colors.borderLight : colors.border) : colors.border, 
                true: colors.primary 
              }}
              thumbColor={startConversation ? colors.white : colors.text.secondary}
              disabled={messageLimits ? !messageLimits.canSend : false}
            />
          </View>

          {/* Topic Field - Only shown when start conversation is enabled */}
          {startConversation && (
            <Input
              label="Conversation Topic *"
              value={topic}
              onChangeText={setTopic}
              placeholder="Enter topic or reason for conversation"
              multiline
              numberOfLines={3}
              error={!topic.trim() && isLoading ? 'Topic is required' : undefined}
            />
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.footer}>
          <Button
            variant="outline"
            onPress={() => navigateTo('home')}
            disabled={isLoading}
            style={styles.cancelButton}
            title="Cancel"
          />
          <Button
            variant="primary"
            onPress={handleSave}
            disabled={
              isLoading || 
              !showContactNameField || 
              !contactName.trim() || 
              !phoneNumber.trim() || 
              (startConversation && !topic.trim()) ||
              (messageLimits ? !messageLimits.canSend && startConversation : false)
            }
            loading={isLoading}
            style={styles.saveButton}
            title={startConversation ? 'Start Conversation' : 'Add Contact'}
          />
        </View>
      </ScrollView>

      {/* Bottom Tabs for Mobile */}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  contentContainer: {
    flexGrow: 1,
    paddingHorizontal: isMobileBrowser ? spacing.screenPadding : (Platform.OS === 'web' ? Math.min(screenWidth * 0.1, 120) : spacing.screenPadding),
    paddingVertical: spacing.lg,
    paddingBottom: 100, // Match other screens padding for bottom tabs
    maxWidth: isMobileBrowser ? undefined : (Platform.OS === 'web' ? 1400 : undefined),
    alignSelf: isMobileBrowser ? undefined : (Platform.OS === 'web' ? 'center' : undefined),
    width: isMobileBrowser ? undefined : (Platform.OS === 'web' ? '100%' : undefined),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.lg,
    marginBottom: spacing.lg,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  whatsappIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#25D366',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  businessInfo: {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border,
  },
  businessName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  businessSubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  formContainer: {
    gap: spacing.lg,
    marginBottom: spacing.xl,
  },
  phoneInputSection: {
    marginBottom: spacing.sm,
  },
  phoneLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  phoneInput: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  existingContactWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#D4EDDA',
    padding: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    marginTop: spacing.sm,
    borderWidth: 1,
    borderColor: '#C3E6CB',
  },
  existingContactText: {
    fontSize: typography.fontSize.sm,
    color: colors.success,
    marginLeft: spacing.xs,
    fontFamily: typography.fontFamily.primary,
    flex: 1,
  },
  checkingContact: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  checkingContactText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginLeft: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  toggleSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  toggleLabel: {
    flex: 1,
    marginRight: spacing.md,
  },
  toggleTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  toggleSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.lg,
    gap: spacing.md,
  },
  cancelButton: {
    flex: 1,
  },
  saveButton: {
    flex: 1,
    backgroundColor: '#25D366',
  },
  disabledInput: {
    backgroundColor: colors.background.secondary,
    opacity: 0.7,
  },
  limitsContainer: {
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  limitsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  limitsTitle: {
    fontSize: 14,
    fontFamily: typography.fontFamily.primary,
    fontWeight: '600',
    color: colors.text.primary,
  },
  limitsCount: {
    fontSize: 14,
    fontFamily: typography.fontFamily.primary,
    fontWeight: '600',
    color: colors.text.primary,
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: colors.border,
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: spacing.sm,
  },
  progressBar: {
    height: '100%',
    borderRadius: 3,
  },
  limitsMessage: {
    fontSize: 12,
    fontFamily: typography.fontFamily.primary,
    color: colors.text.secondary,
  },
  limitReachedText: {
    fontSize: 12,
    fontFamily: typography.fontFamily.primary,
    color: colors.error,
    marginTop: spacing.xs,
    fontWeight: '500',
  },
});
